import { Award, Clock, HeartHandshake, ShieldCheck } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { ROYAL_IMG, RoyalCTA, RoyalLiquidBg, RoyalSectionHead } from "@/components/royal/shared";

const VALUES = [
  { icon: ShieldCheck, title: "Medical-grade standards", desc: "Hospital-level hygiene, sterile protocols and certified devices in every suite." },
  { icon: HeartHandshake, title: "Honest guidance", desc: "We recommend only what serves your skin — never a treatment you don't need." },
  { icon: Award, title: "Doctor-led artistry", desc: "Physician hands and an editor's eye for subtle, natural-looking results." },
  { icon: Clock, title: "Unhurried care", desc: "Long consultations, thoughtful plans and follow-up that actually follows up." },
];

// Royal Signature — About: liquid full-bleed header, values, lead physician.
export default function AboutRoyal() {
  const { content } = useContent();
  const s = content?.settings || {};
  const doctor = (content?.doctors || [])[0];
  const heroBlur = parseInt(s.royal_hero_blur || "0", 10) || 0;

  return (
    <div className="bg-[#FAF8F5]" data-testid="about-page">
      <section className="relative flex min-h-[56vh] items-center overflow-hidden bg-[#F1EBE2]" data-testid="about-hero">
        <RoyalLiquidBg src={s.royal_difference_image || ROYAL_IMG.botanical} blur={heroBlur} testid="about-hero-image" />
        <div className="relative mx-auto w-full max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <div className="max-w-xl py-16 lg:py-20">
            <Reveal>
              <p className="eyebrow-royal" data-testid="about-eyebrow">About Royal Skin Clinic</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h1 className="mt-4 font-serif text-4xl font-light leading-[1.08] tracking-tight text-[#0F0F0F] sm:text-5xl lg:text-6xl" data-testid="about-title">
                Where science meets aesthetics
              </h1>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-sm font-normal leading-relaxed text-[#3A3630] sm:text-base">
                {s.about_intro ||
                  "Royal Skin Clinic is a doctor-led aesthetic practice in Dubai Healthcare City. We believe great work is invisible — skin that looks rested, features that look like yours, confidence that looks effortless."}
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <p className="mt-4 max-w-lg text-sm font-normal leading-relaxed text-[#3A3630] sm:text-base">
                Every plan begins with listening: your concerns, your features, your life. Then medicine, technology and
                artistry — in that order — shape a protocol designed only for you.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white" data-testid="about-values-section">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-10 lg:py-24">
          <RoyalSectionHead eyebrow="What We Stand For" title="The standard behind the glow" testid="about-values" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="about-values-grid">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="royal-card h-full rounded-2xl p-7">
                  <v.icon className="h-6 w-6 text-[#C5A059]" strokeWidth={1.5} aria-hidden="true" />
                  <h3 className="mt-4 font-serif text-xl font-normal text-[#0F0F0F]">{v.title}</h3>
                  <p className="mt-2.5 text-xs font-light leading-relaxed text-[#8A8580]">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {doctor && (
        <section className="bg-[#F5F0EB]" data-testid="about-doctor-section">
          <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:px-10 lg:py-24">
            <div>
              <RoyalSectionHead eyebrow="Lead Physician" title={doctor.name} testid="about-doctor" />
              <Reveal delay={0.2}>
                <p className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-[#8A8580]">{doctor.role}</p>
                <p className="mt-6 max-w-lg text-sm font-normal leading-relaxed text-[#4A4A4A] sm:text-base">{doctor.bio}</p>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <div className="overflow-hidden rounded-[2rem]">
                <img src={doctor.image} alt={`Portrait of ${doctor.name}`} loading="lazy" className="aspect-[4/3] w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <RoyalCTA />
    </div>
  );
}
